"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import "react-calendar/dist/Calendar.css"
import "react-multi-carousel/lib/styles.css"
import { appInsights } from "./AppInsights"
import AADHeader from "./components/AADHeader.js"
import LoginHeader from "./components/LoginHeader.js"
import SideNavigation from "./components/SideNavigation.js"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import HighchartsExporting from "highcharts/modules/exporting"
import HighchartsFullscreen from "highcharts/modules/full-screen"
import HighchartsMap from "highcharts/modules/map"
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting"
import SolidGauge from "highcharts/modules/solid-gauge"
import highchartsMore from "highcharts/highcharts-more"
import mapDataWorld from "@highcharts/map-collection/custom/world.geo.json"
import proj4 from "proj4"
import { MultiSelect } from "primereact/multiselect"
import { ResponsiveContainer } from "recharts"
import { Backdrop, Breadcrumbs, Button, CircularProgress, Link, Typography } from "@mui/material"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import { MdRefresh } from "react-icons/md"

// No Data Component
const NoDataComponent = ({ title, onConfigureClick }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "280px",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px dashed #dee2e6",
      textAlign: "center",
      padding: "20px",
    }}
  >
    <div style={{ marginBottom: "20px" }}>
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M9 17H7V10H9M13 17H11V7H13M17 17H15V13H17M19.5 19.1H4.5V5H19.5M19.5 3H4.5C3.4 3 2.5 3.9 2.5 5V19.1C2.5 20.2 3.4 21.1 4.5 21.1H19.5C20.6 21.1 21.5 20.2 21.5 19.1V5C21.5 3.9 20.6 3 19.5 3Z"
          fill="#6c757d"
        />
      </svg>
    </div>
    <h6 style={{ color: "#6c757d", marginBottom: "10px", fontFamily: "Poppins" }}>No Data Available</h6>
    <p style={{ color: "#6c757d", marginBottom: "20px", fontSize: "14px" }}>Please configure the data for visual</p>
    <Button
      variant="contained"
      onClick={onConfigureClick}
      style={{
        backgroundColor: "#407775",
        color: "white",
        textTransform: "none",
        fontFamily: "Poppins",
        borderRadius: "6px",
        padding: "8px 20px",
      }}
    >
      Configure Data
    </Button>
  </div>
)

// Initialize the exporting module
HighchartsExporting(Highcharts)
HighchartsOfflineExporting(Highcharts)
HighchartsFullscreen(Highcharts)
HighchartsMap(Highcharts)
highchartsMore(Highcharts)
SolidGauge(Highcharts)
// Global Highcharts options for consistent styling
Highcharts.setOptions({
  colors: [
    "#407775",
    "#fab430",
    "#c8e1e0",
    "#b8d9d8",
    "#b8d9d8",
    "#b8d9d8",
    "#b8d9d8",
    "#fcd283",
    "#fde4b4",
    "#fbc051",
    "#e09405",
    "#fef6e6",
  ],
  chart: {
    style: {
      fontFamily: "Poppins, sans-serif",
    },
    backgroundColor: "#ffffff",
    height: 300, // Set a compact height for all charts
  },
  title: {
    style: {
      fontSize: "16px",
      fontWeight: "400",
      color: "#494949",
    },
    align: "left",
  },
  legend: {
    enabled: true,
    itemStyle: {
      color: "#494949",
      fontWeight: "400",
      fontSize: "12px",
    },
    itemMarginTop: 6,
    itemMarginBottom: 6,
    symbolRadius: 6,
  },
  tooltip: {
    style: {
      fontSize: "12px",
    },
  },
  xAxis: {
    labels: {
      style: {
        fontSize: "12px",
        color: "#707070",
      },
    },
  },
  yAxis: {
    labels: {
      style: {
        fontSize: "12px",
        color: "#707070",
      },
    },
    title: {
      style: {
        fontSize: "14px",
        color: "#C2BEBE",
      },
    },
  },
  credits: {
    enabled: false, // Disable Highcharts credit label
  },
  exporting: {
    enabled: false, // Hide export/download button
  },
})

// CSS for compact chart containers
const chartContainerStyle = `
  .charts {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 15px 24px; /* Increased horizontal padding */
    margin-bottom: 20px;
    height: 350px; /* Fixed height for compact charts */
    overflow: hidden;
    position: relative;
  }
  @media (max-width: 768px) {
    .charts {
      height: 300px; /* Smaller height for mobile */
    }
  }
`

// Inject CSS into the document
const styleSheet = document.createElement("style")
styleSheet.type = "text/css"
styleSheet.innerText = chartContainerStyle
document.head.appendChild(styleSheet)

function getFilterOptionValues(options) {
  if (!Array.isArray(options)) return []
  return options.map((opt) => {
    if (opt && typeof opt === "object" && "value" in opt) return String(opt.value)
    return String(opt)
  })
}

function normalizeFilterSelection(selected, allOptions) {
  if (!Array.isArray(selected) || selected.length === 0) return []
  const selectedValues = selected.map((item) =>
    item && typeof item === "object" && "value" in item ? String(item.value) : String(item)
  )
  const optionValues = getFilterOptionValues(allOptions)
  if (optionValues.length === 0) return selectedValues
  if (selectedValues.length !== optionValues.length) return selectedValues
  const selectedSet = new Set(selectedValues.map((value) => value.toLowerCase()))
  const allSelected = optionValues.every((option) => selectedSet.has(String(option).toLowerCase()))
  return allSelected ? [] : selectedValues
}

function readStoredFilter(key) {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null || raw === "") return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map((item) =>
      item && typeof item === "object" && "value" in item ? String(item.value) : String(item)
    )
  } catch {
    return []
  }
}

export default function Home() {
  const username = localStorage.getItem("userName")
  const companyid = localStorage.getItem("companyid")
  const navigate = useNavigate()
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768)
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = React.useState(false)
  const [emissionbased, setEmissionBased] = React.useState(false)
  const [news, setNews] = useState(false)
  const [responseConfig, setResponseConfig] = useState({})
  const [legal, setLegal] = useState([])
  const [business, setBusiness] = useState([])
  const [site, setSite] = useState([])
  const [year, setYear] = useState([])
  const [country, setCountry] = useState([])
  const [employee, setEmployee] = useState("")
  const [scope, setScope] = useState("")
  const [yearr, setYearR] = useState("")
  const [countries, setCountries] = useState()
  const [businessunit, setBusinessUnit] = useState()
  const [activesite, setActiveSite] = useState()
  const [activeetities, setActiveEntities] = useState()
  const [activeyears, setActiveYears] = useState()
  const [checked, setChecked] = useState(true)
  const [viewMode, setViewMode] = useState("locationBased")
  const [optionsChart4, setOptionsChart4] = useState({})
  const [optionsChart6, setOptionsChart6] = useState({})

  // Add state for last refresh time
  const [lastRefreshTime, setLastRefreshTime] = useState(null)

  const currentYear = new Date().getFullYear()
  const previousYear = currentYear - 1

  // Function to handle configuration redirect
  const handleConfigureClick = () => {
    navigate("/configuration") // Update this path to your actual configuration route
  }

  // Function to check if data exists and has values
  const hasValidData = (data) => {
    if (!data || typeof data !== "object") return false
    if (Array.isArray(data)) return data.length > 0
    return Object.keys(data).length > 0 && Object.values(data).some((value) => value > 0)
  }

  if (typeof window !== "undefined") {
    window.proj4 = window.proj4 || proj4
  }

  // Function to format date and time
  const formatDateTime = (date) => {
    if (!date) return ""
    const options = {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }
    return date.toLocaleString("en-US", options)
  }

  // Function to handle toggle change
  const handleToggleChange = (value) => {
    console.log(value)
    if (value === true) {
      setViewMode("locationBased")
      setChecked(value)
    } else {
      setViewMode("marketBased")
      setChecked(value)
    }
  }

  function truncateText(text, maxLength) {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "..."
    }
    return text
  }

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false) // Close sidebar on mobile
      } else {
        setIsSidebarOpen(true) // Open sidebar on larger screens
      }
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  var myHeaders = new Headers()
  myHeaders.append("username", username)
  var requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  }

  const emissionsData = {
    BusinessTravel: {
      cYear: { value: scope?.businessTravelEmissionsYearwise?.currentYear || 0 },
      pYear: { value: scope?.businessTravelEmissionsYearwise?.previousYear || 0 },
      change: scope?.businessTravelEmissionsYearwise?.Change || 0,
      icon: "../assets/img/icons 1/EmployeeCommute.svg",
    },
    Travel: {
      cYear: { value: scope?.travelEmissionsYearwise?.currentYear || 0 },
      pYear: { value: scope?.travelEmissionsYearwise?.previousYear || 0 },
      change: scope?.travelEmissionsYearwise?.Change || 0,
      icon: "../assets/img/icons 1/Travel_emi.svg",
    },
    Hotel: {
      cYear: { value: scope?.hotelEmissionsYearwise?.currentYear || 0 },
      pYear: { value: scope?.hotelEmissionsYearwise?.previousYear || 0 },
      change: scope?.hotelEmissionsYearwise?.Change || 0,
      icon: "../assets/img/icons 1/Hotel_emi.svg",
    },
  }

  // Data validation for each chart
  const hasTravelModeData = hasValidData(scope?.emissionsByTravelMode)
  const hasBusinessUnitsData = hasValidData(scope?.emissionsByBusinessUnits)
  const hasLegalEntityData = hasValidData(scope?.emissionsByLegalEntity)
  const hasAirEmissionsData = hasValidData(scope?.airEmissionsByDestination)
  const hasClassOfAirTravelData =
    hasValidData(scope?.emissionsByClassOfAirTravel) && hasValidData(scope?.noOfTripsByClassOfAirTravel)
  const hasHotelEmissionsData = hasValidData(scope?.hotelEmissionsByDestination)

  async function fetchFilters() {
    try {
      const countrySelected = readStoredFilter("businessTravelCountry")
      const yearSelected = readStoredFilter("businessTravelYear")
      const legalSelected = readStoredFilter("businessTravelLegal")
      const businessSelected = readStoredFilter("businessTravelBusiness")
      const siteSelected = readStoredFilter("businessTravelSite")

      const country = normalizeFilterSelection(countrySelected, countries)
      const year = normalizeFilterSelection(yearSelected, activeyears)
      const legal = normalizeFilterSelection(legalSelected, activeetities)
      const business = normalizeFilterSelection(businessSelected, businessunit)
      const site = normalizeFilterSelection(siteSelected, activesite)

      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}api-business-travel-dashboard-filters`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          site: site,
        }),
      })

      if (response.status === 200) {
        const responseJson = await response.json()

        if (responseJson.success === false || responseJson.status === 400) {
          setActiveEntities("")
          setActiveYears("")
          setBusinessUnit("")
          setCountries("")
          setActiveSite("")
        } else {
          // The API returns each dimension as { value: isActive }, where isActive
          // is the cascade flag: true when the value survives the currently selected
          // filters. A dropdown must never filter itself (that would collapse it to
          // the current selection), so when a dimension is selected we show its full
          // option list; otherwise, if any other dimension is selected we show only
          // the cascaded (isActive === true) values, and with no filters we show all.
          const selectionCounts = {
            year: year.length,
            legal: legal.length,
            business: business.length,
            country: country.length,
            site: site.length,
          }
          const anyOtherSelected = (self) =>
            Object.entries(selectionCounts).some(([name, count]) => name !== self && count > 0)

          const buildOptions = (dict, self) => {
            const cascade = selectionCounts[self] === 0 && anyOtherSelected(self)
            return Object.entries(dict || {})
              .filter(([, isActive]) => (cascade ? isActive === true : true))
              .map(([key]) => ({ label: key, value: key }))
          }

          const yearOptions = buildOptions(responseJson.year, "year")
          const legalOptions = buildOptions(responseJson.legalEntity, "legal")
          const businessOptions = buildOptions(responseJson.businessUnit, "business")
          const countryOptions = buildOptions(responseJson.country, "country")
          const siteOptions = buildOptions(responseJson.site, "site")

          // Set the dropdown options
          setActiveYears(yearOptions)
          setActiveEntities(legalOptions)
          setBusinessUnit(businessOptions)
          setCountries(countryOptions)
          setActiveSite(siteOptions)

          // Keep the user's visible selection; API already received the normalized (Select All => All) payload
          localStorage.setItem("businessTravelLegal", JSON.stringify(legalSelected))
          setLegal(legalSelected)

          localStorage.setItem("businessTravelBusiness", JSON.stringify(businessSelected))
          setBusiness(businessSelected)

          localStorage.setItem("businessTravelCountry", JSON.stringify(countrySelected))
          setCountry(countrySelected)

          localStorage.setItem("businessTravelSite", JSON.stringify(siteSelected))
          setSite(siteSelected)

          localStorage.setItem("businessTravelYear", JSON.stringify(yearSelected))
          setYear(yearSelected)
        }
      } else {
        setActiveEntities("")
        setActiveYears("")
        setBusinessUnit("")
        setCountries("")
        setActiveSite("")
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchScope2() {
    try {
      setLoading(true)
      const companyid = localStorage.getItem("companyid")
      const country = normalizeFilterSelection(readStoredFilter("businessTravelCountry"), countries)
      const year = normalizeFilterSelection(readStoredFilter("businessTravelYear"), activeyears)
      const legal = normalizeFilterSelection(readStoredFilter("businessTravelLegal"), activeetities)
      const business = normalizeFilterSelection(readStoredFilter("businessTravelBusiness"), businessunit)
      const site = normalizeFilterSelection(readStoredFilter("businessTravelSite"), activesite)
      var myHeaders = new Headers()
      myHeaders.append("username", username)
      var requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      }
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}api-business-travel-dashboard`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          site: site,
        }),
      })
      if (response.status === 200) {
        const responseJson = await response.json()
        if (responseJson.success === false || responseJson.status === 400) {
          setLoading(false)
        } else {
          setScope(responseJson)
          // Update last refresh time when data is successfully fetched
          setLastRefreshTime(new Date())
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }

  useEffect(() => {
    appInsights.trackPageView()
    fetchFilters()
    fetchScope2()
  }, [])

  const handleLegalChange = (selectedLegal) => {
    setLegal(selectedLegal)
    localStorage.setItem("businessTravelLegal", JSON.stringify(selectedLegal))
    fetchScope2()
    fetchFilters()
  }

  const handleBusinessChange = (selectedBusiness) => {
    setBusiness(selectedBusiness)
    localStorage.setItem("businessTravelBusiness", JSON.stringify(selectedBusiness))
    fetchScope2()
    fetchFilters()
  }

  const handleCountryChange = (selectedCountry) => {
    setCountry(selectedCountry)
    localStorage.setItem("businessTravelCountry", JSON.stringify(selectedCountry))
    fetchScope2()
    fetchFilters()
  }

  const handleSiteChange = (selectedSite) => {
    setSite(selectedSite)
    localStorage.setItem("businessTravelSite", JSON.stringify(selectedSite))
    fetchScope2()
    fetchFilters()
  }

  const handleYearChange = (selectedYear) => {
    setYear(selectedYear)
    localStorage.setItem("businessTravelYear", JSON.stringify(selectedYear))
    fetchScope2()
    fetchFilters()
  }

  // Consuption by supplier start
  const consumptionBySupplier = scope && scope.consumptionBySupplier

  const seriesData = consumptionBySupplier
    ? Object.entries(consumptionBySupplier).map(([supplierName, electricityConsumption]) => ({
        name: supplierName,
        y: Number.parseFloat(electricityConsumption),
      }))
    : []
  const seriesData_location = consumptionBySupplier
    ? Object.entries(consumptionBySupplier).map(([supplierName, electricityConsumption]) => ({
        name: supplierName,
        y: Number.parseFloat(electricityConsumption),
      }))
    : []

  const activeyear = activeyears && activeyears.map((status) => status)
  console.log(activeyear)
  const activelegal = activeetities && activeetities.map((status) => status)
  const activebusiness = businessunit && businessunit.map((status) => status)
  const activesites = activesite && activesite.map((status) => status)
  const activecountry = countries && countries.map((status) => status)
  console.log(activecountry)
  //const activeyear = yearss.map((status) => status.name);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType")
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
  }

  const handleResetFilters = async () => {
    try {
      setLoading(true)
      // Clear all selected filters in state
      setLegal([])
      setBusiness([])
      setCountry([])
      setSite([])
      setYear([])

      // Clear all selected filters in local storage
      localStorage.removeItem("businessTravelLegal")
      localStorage.removeItem("businessTravelBusiness")
      localStorage.removeItem("businessTravelCountry")
      localStorage.removeItem("businessTravelSite")
      localStorage.removeItem("businessTravelYear")

      // Fetch filters again to reset dropdowns to show all values
      await fetchFilters()

      // Fetch scope 2 data again with no filters applied
      await fetchScope2()
    } catch (error) {
      console.error("Error resetting filters:", error)
    }
  }

  // Handle manual refresh
  const handleRefresh = () => {
    fetchScope2()
    fetchFilters()
  }

  const [selectedOption, setSelectedOption] = useState("Upstream")

  const handleSelect = (option) => {
    setSelectedOption(option)
  }
  //chart1
  const chartConfig = {
    chart: {
      type: "pie",
      style: {
        fontFamily: "poppins",
      },
    },
    title: {
      text: "Emissions by Travel Mode",
      align: "left",
      style: {
        fontSize: "16px",
        fontWeight: "400",
        color: "#494949",
      },
    },
    tooltip: {
      pointFormat: "Emission: <b>{point.y}%</b>",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        size: "65%",

        dataLabels: {
          enabled: true,
          format: "{point.y}%",
          connectorShape: "fixedOffset",
          connectorColor: "#707070",
          style: {
            color: "#707070",
            fontSize: "12px",
            fontWeight: "400",
          },
        },
        showInLegend: true,
      },
    },
    legend: {
      enabled: document.documentElement.clientWidth >= 768,
      layout: "vertical",
      align: "left",
      verticalAlign: "middle",
      borderWidth: 0,
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      itemMarginTop: 6,
      itemMarginBottom: 6,
      symbolRadius: 10,
      y: -50,
      x: -10,
    },
    series: [
      {
        name: "Travel Mode",
        data: scope?.emissionsByTravelMode
          ? Object.entries(scope.emissionsByTravelMode).map(([key, value]) => {
              const colors = {
                Flight: "#ADA9A9",
                Rail: "#E0E0E0",
                Bus: "#A3C084",
                Taxi: "#4F8C79",
                Hotel: "#F1B700",
              }
              return {
                name: key,
                y: value || 0,
                color: colors[key] || "#ADA9A9",
              }
            })
          : [],
      },
    ],
  }

  //chart2
  const emissionsDataBu = {
    title: "Emissions (tCO2e) by business units",
    yAxisTitle: "Emissions (tCO2e)",
    color: "#B4A34A",
    categories: scope?.emissionsByBusinessUnits ? Object.keys(scope.emissionsByBusinessUnits) : [],
    values: scope?.emissionsByBusinessUnits ? Object.values(scope.emissionsByBusinessUnits) : [],
  }
  const chartOptions = [
    {
      id: "emissionData",
      options: {
        chart: {
          type: "bar",
          backgroundColor: "#ffffff",
        },
        title: {
          text: emissionsDataBu.title,
          align: "left",
          style: {
            fontSize: "16px",
            color: "#494949",
            fontWeight: "normal",
          },
        },
        xAxis: {
          categories: emissionsDataBu.categories,
          title: {
            text: null,
          },
          labels: {
            style: {
              fontSize: "12px",
              color: "#707070",
            },
            formatter: function () {
              return this.value.length > 15 ? this.value.substring(0, 15) + "..." : this.value
            },
          },
        },
        yAxis: {
          min: 0,
          title: {
            text: emissionsDataBu.yAxisTitle,
            align: "middle",
            style: {
              fontSize: "15px",
              color: "#C2BEBE",
            },
          },
          labels: {
            enabled: false,
          },
        },
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: true,
              style: {
                fontSize: "12px",
                color: "#494949",
                fontWeight: "normal",
              },
            },
          },
        },
        series: [
          {
            name: "Emissions",
            data: emissionsDataBu.values,
            showInLegend: false,
            color: emissionsDataBu.color,
          },
        ],
      },
    },
  ]
  //chart3
  const annualEmissionSite = {
    title: "Emissions (tCO2e) by legal entity",
    subtitle: "Emissions (tCO2e)",
    data: scope?.emissionsByLegalEntity
      ? Object.entries(scope.emissionsByLegalEntity).map(([name, value]) => ({
          name: name,
          y: value || 0,
        }))
      : [],
    color: "#B4C9C8",
  }
  const annualEmissionSiteChartConfig = {
    chart: {
      type: "column",
      style: {},
    },
    title: {
      text: annualEmissionSite.title,
      align: "left",
      style: {
        fontWeight: "normal",
        fontSize: "16px",
        color: "#494949",
      },
    },
    plotOptions: {
      column: {
        pointWidth: 40,
        borderRadius: "0%",
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "normal",
            fontSize: "14px",
            color: "#707070",
          },
          format: "{point.y}",
          groupPadding: 4,
        },
      },
    },
    xAxis: {
      categories: annualEmissionSite.data.map((item) => item.name),
      gridLineWidth: 1,
      labels: {
        style: {
          marginTop: "100px",
          fontSize: "12px",
          color: "#707070",
        },
        formatter: function () {
          return this.value.length > 15 ? this.value.substring(0, 15) + "..." : this.value
        },
        y: 15,
      },
    },
    yAxis: {
      tickInterval: 1000,
      title: {
        text: "Emissions (tCO2e)",
        offset: 60,
        style: {
          fontSize: "16px",
          color: "#C2BEBE",
        },
        x: -7,
      },
      labels: {
        formatter: function () {
          return this.value
        },
        style: {
          fontSize: "12px",
          color: "#707070",
        },
        x: -20,
      },
      gridLineWidth: 0,
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: "Average Distance",
        data: annualEmissionSite.data.map((item) => ({
          y: item.y,
          color: annualEmissionSite.color,
        })),
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 768,
          },
          chartOptions: {
            plotOptions: {
              column: {
                pointWidth: 30,
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 425,
          },
          chartOptions: {
            chart: {
              spacing: [20, 20, 20, 20],
            },
            title: {
              style: {
                fontSize: "18px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
            yAxis: {
              title: {
                style: {
                  fontSize: "14px",
                },
              },
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
            plotOptions: {
              column: {
                dataLabels: {
                  style: {
                    fontSize: "12px",
                  },
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 320,
          },
          chartOptions: {
            chart: {
              spacing: [0, 0, 0, 0],
            },
            title: {
              style: {
                fontSize: "14px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "8px",
                },
              },
            },
            yAxis: {
              title: {
                style: {
                  fontSize: "12px",
                },
              },
              labels: {
                style: {
                  fontSize: "8px",
                },
              },
            },
            plotOptions: {
              column: {
                dataLabels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            },
          },
        },
      ],
    },
  }

  //chart4
  useEffect(() => {
    // Function to convert country name to ISO code
    const getCountryCode = (countryName) => {
      const countryMap = {
        // Europe
        Albania: "AL",
        Andorra: "AD",
        Austria: "AT",
        Belarus: "BY",
        Belgium: "BE",
        "Bosnia and Herzegovina": "BA",
        Bulgaria: "BG",
        Croatia: "HR",
        "Czech Republic": "CZ",
        Denmark: "DK",
        Estonia: "EE",
        Finland: "FI",
        France: "FR",
        Germany: "DE",
        Greece: "GR",
        Hungary: "HU",
        Iceland: "IS",
        Ireland: "IE",
        Italy: "IT",
        Latvia: "LV",
        Liechtenstein: "LI",
        Lithuania: "LT",
        Luxembourg: "LU",
        Malta: "MT",
        Moldova: "MD",
        Monaco: "MC",
        Montenegro: "ME",
        Netherlands: "NL",
        "North Macedonia": "MK",
        Norway: "NO",
        Poland: "PL",
        Portugal: "PT",
        Romania: "RO",
        Russia: "RU",
        "San Marino": "SM",
        Serbia: "RS",
        Slovakia: "SK",
        Slovenia: "SI",
        Spain: "ES",
        Sweden: "SE",
        Switzerland: "CH",
        Ukraine: "UA",
        "United Kingdom": "GB",
        "Vatican City": "VA",

        // Asia
        Afghanistan: "AF",
        Armenia: "AM",
        Azerbaijan: "AZ",
        Bahrain: "BH",
        Bangladesh: "BD",
        Bhutan: "BT",
        Brunei: "BN",
        Cambodia: "KH",
        China: "CN",
        Cyprus: "CY",
        Georgia: "GE",
        India: "IN",
        Indonesia: "ID",
        Iran: "IR",
        Iraq: "IQ",
        Israel: "IL",
        Japan: "JP",
        Jordan: "JO",
        Kazakhstan: "KZ",
        Kuwait: "KW",
        Kyrgyzstan: "KG",
        Laos: "LA",
        Lebanon: "LB",
        Malaysia: "MY",
        Maldives: "MV",
        Mongolia: "MN",
        Myanmar: "MM",
        Nepal: "NP",
        "North Korea": "KP",
        Oman: "OM",
        Pakistan: "PK",
        Palestine: "PS",
        Philippines: "PH",
        Qatar: "QA",
        "Saudi Arabia": "SA",
        Singapore: "SG",
        "South Korea": "KR",
        "Sri Lanka": "LK",
        Syria: "SY",
        Taiwan: "TW",
        Tajikistan: "TJ",
        Thailand: "TH",
        "Timor-Leste": "TL",
        Turkey: "TR",
        Turkmenistan: "TM",
        "United Arab Emirates": "AE",
        Uzbekistan: "UZ",
        Vietnam: "VN",
        Yemen: "YE",

        // Americas
        "Antigua and Barbuda": "AG",
        Argentina: "AR",
        Bahamas: "BS",
        Barbados: "BB",
        Belize: "BZ",
        Bolivia: "BO",
        Brazil: "BR",
        Canada: "CA",
        Chile: "CL",
        Colombia: "CO",
        "Costa Rica": "CR",
        Cuba: "CU",
        Dominica: "DM",
        "Dominican Republic": "DO",
        Ecuador: "EC",
        "El Salvador": "SV",
        Grenada: "GD",
        Guatemala: "GT",
        Guyana: "GY",
        Haiti: "HT",
        Honduras: "HN",
        Jamaica: "JM",
        Mexico: "MX",
        Nicaragua: "NI",
        Panama: "PA",
        Paraguay: "PY",
        Peru: "PE",
        "Saint Kitts and Nevis": "KN",
        "Saint Lucia": "LC",
        "Saint Vincent and the Grenadines": "VC",
        Suriname: "SR",
        "Trinidad and Tobago": "TT",
        "United States": "US",
        Uruguay: "UY",
        Venezuela: "VE",

        // Africa
        Algeria: "DZ",
        Angola: "AO",
        Benin: "BJ",
        Botswana: "BW",
        "Burkina Faso": "BF",
        Burundi: "BI",
        "Cabo Verde": "CV",
        Cameroon: "CM",
        "Central African Republic": "CF",
        Chad: "TD",
        Comoros: "KM",
        Congo: "CG",
        "Congo (Democratic Republic)": "CD",
        Djibouti: "DJ",
        Egypt: "EG",
        "Equatorial Guinea": "GQ",
        Eritrea: "ER",
        Eswatini: "SZ",
        Ethiopia: "ET",
        Gabon: "GA",
        Gambia: "GM",
        Ghana: "GH",
        Guinea: "GN",
        "Guinea-Bissau": "GW",
        "Ivory Coast": "CI",
        Kenya: "KE",
        Lesotho: "LS",
        Liberia: "LR",
        Libya: "LY",
        Madagascar: "MG",
        Malawi: "MW",
        Mali: "ML",
        Mauritania: "MR",
        Mauritius: "MU",
        Morocco: "MA",
        Mozambique: "MZ",
        Namibia: "NA",
        Niger: "NE",
        Nigeria: "NG",
        Rwanda: "RW",
        "Sao Tome and Principe": "ST",
        Senegal: "SN",
        Seychelles: "SC",
        "Sierra Leone": "SL",
        Somalia: "SO",
        "South Africa": "ZA",
        "South Sudan": "SS",
        Sudan: "SD",
        Tanzania: "TZ",
        Togo: "TG",
        Tunisia: "TN",
        Uganda: "UG",
        Zambia: "ZM",
        Zimbabwe: "ZW",

        // Oceania
        Australia: "AU",
        Fiji: "FJ",
        Kiribati: "KI",
        "Marshall Islands": "MH",
        Micronesia: "FM",
        Nauru: "NR",
        "New Zealand": "NZ",
        Palau: "PW",
        "Papua New Guinea": "PG",
        Samoa: "WS",
        "Solomon Islands": "SB",
        Tonga: "TO",
        Tuvalu: "TV",
        Vanuatu: "VU",
      }

      // Try exact match first
      if (countryMap[countryName]) {
        return countryMap[countryName]
      }

      // Try case-insensitive match
      const normalizedCountryName = countryName.toLowerCase()
      const foundCountry = Object.keys(countryMap).find((key) => key.toLowerCase() === normalizedCountryName)
      if (foundCountry) {
        return countryMap[foundCountry]
      }

      // Try partial match
      const partialMatch = Object.keys(countryMap).find(
        (key) => key.toLowerCase().includes(normalizedCountryName) || normalizedCountryName.includes(key.toLowerCase()),
      )
      if (partialMatch) {
        return countryMap[partialMatch]
      }

      return null
    }

    // Convert API data to map format
    const mapData = scope?.airEmissionsByDestination
      ? Object.entries(scope.airEmissionsByDestination)
          .map(([country, value]) => ({
            code: getCountryCode(country),
            value: value || 0,
            name: country,
          }))
          .filter((item) => item.code !== null) // Remove countries without ISO codes
      : []

    // Calculate min and max values for color axis
    const values = mapData.map((item) => item.value)
    const minValue = Math.min(...values) || 0
    const maxValue = Math.max(...values) || 1000

    setOptionsChart4({
      chart: {
        map: mapDataWorld,
        backgroundColor: "transparent",
      },
      title: {
        text: "Air Emissions (tCO2e) by Destination",
        align: "left",
        style: { color: "#494949", fontSize: "16px", fontWeight: "normal" },
      },
      subtitle: {
        text: "",
        style: { color: "#4B5563" },
      },
      colorAxis: {
        width: "20%",
        height: 25,
        margin: 10,
        min: minValue,
        max: maxValue,
        stops: [
          [0, "#FAB430"],
          [1, "#407775"],
        ],
      },
      legend: {
        layout: "horizontal",
        align: "right",
        verticalAlign: "bottom",
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          alignTo: "spacingBox",
          align: "right",
          verticalAlign: "top",
          x: -10,
          y: 30,
        },
      },
      series: [
        {
          data: mapData,
          mapData: mapDataWorld,
          joinBy: ["iso-a2", "code"],
          name: "Air Travel Emissions",
          states: { hover: { color: "#407775" }, select: { color: "#407775" } },
          tooltip: {
            pointFormat: "{point.name}: {point.value} tCO2e",
            valueDecimals: 1,
          },
        },
      ],
    })
  }, [scope?.airEmissionsByDestination])

  //chart5
  const chartData = {
    categories: scope?.emissionsByClassOfAirTravel ? Object.keys(scope.emissionsByClassOfAirTravel) : [],
    emissions: scope?.emissionsByClassOfAirTravel ? Object.values(scope.emissionsByClassOfAirTravel) : [],
    trips: scope?.noOfTripsByClassOfAirTravel ? Object.values(scope.noOfTripsByClassOfAirTravel) : [],
    colors: {
      emissions: "#FAB430",
      trips: "#407775",
    },
  }

  const chartOptions2 = {
    chart: {
      type: "column",
      style: {
        fontFamily: "Poppins",
      },
    },
    title: {
      text: "Emissions and No. of Trips by Class of Air Travel",
      style: {
        fontSize: "16px",
        color: "#494949",
        fontWeight: "normal",
      },
    },
    xAxis: {
      categories: chartData.categories,
      title: { text: null },
      labels: {
        style: {
          fontSize: "12px",
          color: "#707070",
        },
        formatter: function () {
          return this.value.length > 15 ? this.value.substring(0, 15) + "..." : this.value
        },
      },
    },
    yAxis: [
      {
        tickInterval: Math.ceil(Math.max(...chartData.emissions) / 5) || 250,
        title: {
          text: "Emissions (tCO2e)",
          style: { fontSize: "14px", color: "#C2BEBE" },
        },
        labels: {
          style: { fontSize: "12px", color: "#707070" },
        },
        opposite: false,
        startOnTick: true,
        endOnTick: true,
      },
      {
        tickInterval: Math.ceil(Math.max(...chartData.trips) / 5) || 2000,
        title: {
          text: "No. of Trips",
          style: { fontSize: "14px", color: "#C2BEBE" },
        },
        labels: {
          formatter: function () {
            return Highcharts.numberFormat(this.value, 0)
          },
          style: { fontSize: "12px", color: "#707070" },
        },
        min: 0,
        startOnTick: true,
        endOnTick: true,
        opposite: true,
      },
    ],
    tooltip: {
      shared: true,
      valueDecimals: 1,
    },
    legend: {
      enabled: document.documentElement.clientWidth >= 768,
      useHTML: true,
      itemStyle: {
        symbolHeight: 12,
        symbolWidth: 12,
        symbolRadius: 6,
      },
    },
    series: [
      {
        name: "Air Travel Emissions (tCO2e)",
        type: "column",
        color: chartData.colors.emissions,
        data: chartData.emissions,
        tooltip: {
          valueSuffix: " tCO2e",
        },
        yAxis: 0,
      },
      {
        name: "No. of Trips",
        type: "line",
        color: chartData.colors.trips,
        data: chartData.trips,
        tooltip: {
          valueSuffix: " trips",
        },
        yAxis: 1,
      },
    ],
  }
  //chart6
  useEffect(() => {
    // Function to convert country name to ISO code
    const getCountryCode = (countryName) => {
      // First try exact match
      const countryMap = {
        // Europe
        Albania: "AL",
        Andorra: "AD",
        Austria: "AT",
        Belarus: "BY",
        Belgium: "BE",
        "Bosnia and Herzegovina": "BA",
        Bulgaria: "BG",
        Croatia: "HR",
        "Czech Republic": "CZ",
        Denmark: "DK",
        Estonia: "EE",
        Finland: "FI",
        France: "FR",
        Germany: "DE",
        Greece: "GR",
        Hungary: "HU",
        Iceland: "IS",
        Ireland: "IE",
        Italy: "IT",
        Latvia: "LV",
        Liechtenstein: "LI",
        Lithuania: "LT",
        Luxembourg: "LU",
        Malta: "MT",
        Moldova: "MD",
        Monaco: "MC",
        Montenegro: "ME",
        Netherlands: "NL",
        "North Macedonia": "MK",
        Norway: "NO",
        Poland: "PL",
        Portugal: "PT",
        Romania: "RO",
        Russia: "RU",
        "San Marino": "SM",
        Serbia: "RS",
        Slovakia: "SK",
        Slovenia: "SI",
        Spain: "ES",
        Sweden: "SE",
        Switzerland: "CH",
        Ukraine: "UA",
        "United Kingdom": "GB",
        "Vatican City": "VA",

        // Asia
        Afghanistan: "AF",
        Armenia: "AM",
        Azerbaijan: "AZ",
        Bahrain: "BH",
        Bangladesh: "BD",
        Bhutan: "BT",
        Brunei: "BN",
        Cambodia: "KH",
        China: "CN",
        Cyprus: "CY",
        Georgia: "GE",
        India: "IN",
        Indonesia: "ID",
        Iran: "IR",
        Iraq: "IQ",
        Israel: "IL",
        Japan: "JP",
        Jordan: "JO",
        Kazakhstan: "KZ",
        Kuwait: "KW",
        Kyrgyzstan: "KG",
        Laos: "LA",
        Lebanon: "LB",
        Malaysia: "MY",
        Maldives: "MV",
        Mongolia: "MN",
        Myanmar: "MM",
        Nepal: "NP",
        "North Korea": "KP",
        Oman: "OM",
        Pakistan: "PK",
        Palestine: "PS",
        Philippines: "PH",
        Qatar: "QA",
        "Saudi Arabia": "SA",
        Singapore: "SG",
        "South Korea": "KR",
        "Sri Lanka": "LK",
        Syria: "SY",
        Taiwan: "TW",
        Tajikistan: "TJ",
        Thailand: "TH",
        "Timor-Leste": "TL",
        Turkey: "TR",
        Turkmenistan: "TM",
        "United Arab Emirates": "AE",
        Uzbekistan: "UZ",
        Vietnam: "VN",
        Yemen: "YE",

        // Americas
        "Antigua and Barbuda": "AG",
        Argentina: "AR",
        Bahamas: "BS",
        Barbados: "BB",
        Belize: "BZ",
        Bolivia: "BO",
        Brazil: "BR",
        Canada: "CA",
        Chile: "CL",
        Colombia: "CO",
        "Costa Rica": "CR",
        Cuba: "CU",
        Dominica: "DM",
        "Dominican Republic": "DO",
        Ecuador: "EC",
        "El Salvador": "SV",
        Grenada: "GD",
        Guatemala: "GT",
        Guyana: "GY",
        Haiti: "HT",
        Honduras: "HN",
        Jamaica: "JM",
        Mexico: "MX",
        Nicaragua: "NI",
        Panama: "PA",
        Paraguay: "PY",
        Peru: "PE",
        "Saint Kitts and Nevis": "KN",
        "Saint Lucia": "LC",
        "Saint Vincent and the Grenadines": "VC",
        Suriname: "SR",
        "Trinidad and Tobago": "TT",
        "United States": "US",
        Uruguay: "UY",
        Venezuela: "VE",

        // Africa
        Algeria: "DZ",
        Angola: "AO",
        Benin: "BJ",
        Botswana: "BW",
        "Burkina Faso": "BF",
        Burundi: "BI",
        "Cabo Verde": "CV",
        Cameroon: "CM",
        "Central African Republic": "CF",
        Chad: "TD",
        Comoros: "KM",
        Congo: "CG",
        "Congo (Democratic Republic)": "CD",
        Djibouti: "DJ",
        Egypt: "EG",
        "Equatorial Guinea": "GQ",
        Eritrea: "ER",
        Eswatini: "SZ",
        Ethiopia: "ET",
        Gabon: "GA",
        Gambia: "GM",
        Ghana: "GH",
        Guinea: "GN",
        "Guinea-Bissau": "GW",
        "Ivory Coast": "CI",
        Kenya: "KE",
        Lesotho: "LS",
        Liberia: "LR",
        Libya: "LY",
        Madagascar: "MG",
        Malawi: "MW",
        Mali: "ML",
        Mauritania: "MR",
        Mauritius: "MU",
        Morocco: "MA",
        Mozambique: "MZ",
        Namibia: "NA",
        Niger: "NE",
        Nigeria: "NG",
        Rwanda: "RW",
        "Sao Tome and Principe": "ST",
        Senegal: "SN",
        Seychelles: "SC",
        "Sierra Leone": "SL",
        Somalia: "SO",
        "South Africa": "ZA",
        "South Sudan": "SS",
        Sudan: "SD",
        Tanzania: "TZ",
        Togo: "TG",
        Tunisia: "TN",
        Uganda: "UG",
        Zambia: "ZM",
        Zimbabwe: "ZW",

        // Oceania
        Australia: "AU",
        Fiji: "FJ",
        Kiribati: "KI",
        "Marshall Islands": "MH",
        Micronesia: "FM",
        Nauru: "NR",
        "New Zealand": "NZ",
        Palau: "PW",
        "Papua New Guinea": "PG",
        Samoa: "WS",
        "Solomon Islands": "SB",
        Tonga: "TO",
        Tuvalu: "TV",
        Vanuatu: "VU",
      }

      // Try exact match first
      if (countryMap[countryName]) {
        return countryMap[countryName]
      }

      // Try case-insensitive match
      const normalizedCountryName = countryName.toLowerCase()
      const foundCountry = Object.keys(countryMap).find((key) => key.toLowerCase() === normalizedCountryName)
      if (foundCountry) {
        return countryMap[foundCountry]
      }

      // Try partial match
      const partialMatch = Object.keys(countryMap).find(
        (key) => key.toLowerCase().includes(normalizedCountryName) || normalizedCountryName.includes(key.toLowerCase()),
      )
      if (partialMatch) {
        return countryMap[partialMatch]
      }

      return null
    }

    // Convert API data to map format
    const mapData = scope?.hotelEmissionsByDestination
      ? Object.entries(scope.hotelEmissionsByDestination)
          .map(([country, value]) => ({
            code: getCountryCode(country),
            value: value || 0,
            name: country,
          }))
          .filter((item) => item.code !== null) // Remove countries without ISO codes
      : []

    // Calculate min and max values for color axis
    const values = mapData.map((item) => item.value)
    const minValue = Math.min(...values) || 0
    const maxValue = Math.max(...values) || 549

    setOptionsChart6({
      chart: {
        map: mapDataWorld,
        backgroundColor: "transparent",
      },
      title: {
        text: "Hotel emissions (tCO2e) by destination",
        align: "left",
        style: {
          color: "#494949",
          fontSize: "16px",
          fontWeight: "normal",
        },
      },

      colorAxis: {
        width: "20%",
        height: 25,
        margin: 10,
        min: minValue,
        max: maxValue,
        stops: [
          [0, "#FAB430"],
          [1, "#407775"],
        ],
      },
      legend: {
        layout: "horizontal",
        align: "right",
        verticalAlign: "bottom",
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          alignTo: "spacingBox",
        },
      },
      series: [
        {
          data: mapData,
          mapData: mapDataWorld,
          joinBy: ["iso-a2", "code"],
          name: "Hotel Emissions",
          states: {
            hover: {
              color: "#407775",
            },
            select: {
              color: "#407775",
            },
          },
          tooltip: {
            pointFormat: "{point.name}: {point.value} tCO2e",
            valueDecimals: 1,
          },
        },
      ],
    })
  }, [scope?.hotelEmissionsByDestination])

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        {/* Rest of your existing JSX content */}
        <div
          class="container"
          style={{ marginLeft: "auto", marginRight: "auto", paddingLeft: "30px", paddingRight: "20px" }}
        >
          <div className="row">
            <div className="col-12 col-md-8">
              <div Class="steps__containt-hader">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h3 class="title mb-0">Business Travel</h3>
                  {/* Add Last Refresh Time Display inline with heading */}
                  {lastRefreshTime && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#707070",
                        fontWeight: "400",
                      }}
                    >
                      Last Refresh: {formatDateTime(lastRefreshTime)}
                    </div>
                  )}
                </div>
                <div role="presentation" className="mt-2">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/esg-dashboard" className="link">
                      Dashboard
                    </Link>
                    <Typography color="text.primary">Business Travel</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-end">
              <div className="row justify-content-md-end my-2">
                <div className="col-auto">
                  <Button className="refresh-cta" variant="text" startIcon={<MdRefresh />} onClick={handleRefresh}>
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {[
              {
                label: "Legal Entity",
                value: legal,
                setValue: setLegal,
                handleChange: handleLegalChange,
                options: activelegal,
              },
              {
                label: "Business Unit",
                value: business,
                setValue: setBusiness,
                handleChange: handleBusinessChange,
                options: activebusiness,
              },
              { label: "Site", value: site, setValue: setSite, handleChange: handleSiteChange, options: activesites },
              {
                label: "Country",
                value: country,
                setValue: setCountry,
                handleChange: handleCountryChange,
                options: activecountry,
              },
              { label: "Year", value: year, setValue: setYear, handleChange: handleYearChange, options: activeyear },
            ].map(({ label, value, setValue, handleChange, options }, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label">{label}</label>
                <MultiSelect
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value)
                    handleChange(e.target.value)
                  }}
                  options={options}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="All"
                  maxSelectedLabels={1}
                  className="w-100"
                  showClear
                  showSelectAll
                  selectAllLabel="Select All"
                />
              </div>
            ))}

            <div className="col-12 col-sm-6 col-md-4 col-lg-2 d-flex align-items-end">
              <Button
                onClick={handleResetFilters}
                variant="outlined"
                startIcon={<MdRefresh />}
                sx={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "400",
                  textTransform: "none",
                  minHeight: "40px",
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#e8e8e8",
                    border: "1px solid #ccc",
                  },
                  "&:focus": {
                    outline: "none",
                    boxShadow: "0 0 0 2px rgba(0,123,255,.25)",
                  },
                }}
              >
                Reset
              </Button>
            </div>
          </div>
          {scope && (
            <div>
              <div className="row mt-4">
                {Object.keys(emissionsData).map((key) => (
                  <div
                    key={key}
                    className="col-12 col-sm-12 col-md-6 col-lg-4 d-flex mb-4"
                    style={{ paddingLeft: "8px", paddingRight: "8px" }}
                  >
                    <div
                      className="bg-white p-3 shadow w-100 d-flex flex-column"
                      style={{
                        maxHeight: "180px",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        paddingLeft: "40px", // Increased horizontal padding
                        paddingRight: "40px", // Increased horizontal padding
                      }}
                    >
                      {/* Icon + Title Row */}
                      <div className="d-flex align-items-center mb-3">
                        <img
                          src={emissionsData[key].icon || "/placeholder.svg"}
                          alt={`${key} icon`}
                          style={{
                            width: "40px",
                            height: "34px",
                            marginRight: "12px",
                            flexShrink: 0,
                          }}
                        />
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#707070",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                            lineHeight: 1.4,
                          }}
                          title={`${key.replace(/([A-Z])/g, " $1").trim()} emissions (tCO₂e)`}
                        >
                          {key.replace(/([A-Z])/g, " $1").trim()} emissions (tCO₂e)
                        </div>
                      </div>

                      {/* Yearly Data */}
                      <div className="d-flex justify-content-between align-items-end">
                        {/* 2023 */}
                        <div className="text-center">
                          <div style={{ fontSize: "12px", color: "#707070" }}>{currentYear}</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#494949" }}>
                            {emissionsData[key].cYear.value.toLocaleString()}
                          </div>
                        </div>

                        {/* 2022 */}
                        <div className="text-center">
                          <div style={{ fontSize: "12px", color: "#707070" }}>{previousYear}</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#494949" }}>
                            {emissionsData[key].pYear.value.toLocaleString()}
                          </div>
                        </div>

                        {/* Change */}
                        <div className="text-center">
                          <div style={{ fontSize: "12px", color: "#707070" }}>Change</div>
                          <div className="d-flex align-items-center justify-content-center">
                            <img
                              src={
                                emissionsData[key].change <= 0
                                  ? "../assets/img/icon/GreenDown.svg"
                                  : "../assets/img/icon/RedUp.svg"
                              }
                              alt="change icon"
                              style={{ width: "20px", height: "18px", marginRight: "4px" }}
                            />
                            <span
                              style={{
                                fontSize: "18px",
                                fontWeight: "600",
                                color: emissionsData[key].change <= 0 ? "#28A745" : "#D9534F",
                              }}
                            >
                              {Math.abs(emissionsData[key].change)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Charts Section */}
              <div className="row">
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    {hasTravelModeData ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={chartConfig} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions by Travel Mode" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    {hasBusinessUnitsData ? (
                      <ResponsiveContainer width="100%">
                        {chartOptions.map((chart) => (
                          <div key={chart.id} className="mb-4">
                            <HighchartsReact highcharts={Highcharts} options={chart.options} />
                          </div>
                        ))}
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions by Business Units" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
              </div>

              {/* Energy Consumption & Emission */}
              <div className="row mt-2">
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    {hasLegalEntityData ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={annualEmissionSiteChartConfig} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions by Legal Entity" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    {hasAirEmissionsData ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} constructorType={"mapChart"} options={optionsChart4} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Air Emissions by Destination" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
              </div>

              {/* Final Emission Graphs */}
              <div className="row mt-2">
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    {hasClassOfAirTravelData ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={chartOptions2} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent
                        title="Emissions and Trips by Class of Air Travel"
                        onConfigureClick={handleConfigureClick}
                      />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    {hasHotelEmissionsData ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} constructorType={"mapChart"} options={optionsChart6} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Hotel Emissions by Destination" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
